<template>
  <v-autocomplete
    v-model="value"
    ref="autocompleteRef"
    :loading="loading"
    :label="label"
    :items="items"
    :item-value="itemValue"
    :item-title="itemTitle"
    :menu-props="{
      contentClass: ['e-autocomplete__content', { 'e-autocomplete__content--error': isError }],
      attach: true,
    }"
    variant="outlined"
    hide-no-data
    hide-details="auto"
    :menu-icon="!showMenuIcon ? '' : undefined"
    auto-select-first
    :multiple="multiple"
    class="e-autocomplete"
    @update:search="onSearchUpdate"
  >
    <template v-if="slots['item']" #item="slotProps">
      <slot 
        name="item" 
        :item="slotProps.item"
        :props="slotProps.props"
        :raw="slotProps.item.raw"
      />
    </template>

    <template v-if="slots['prepend-inner']" #prepend-inner>
      <slot name="prepend-inner" />
    </template>

    <template v-if="hideSelection" #selection />
  </v-autocomplete>
</template>

<script
  setup
  lang="ts"
  generic="
    T extends Record<string, unknown> | string | number | null,
    K extends T extends Record<string, unknown> ? Paths<T> | undefined : undefined,
    M extends boolean | undefined = undefined
  "
>
import { onUpdated, ref } from 'vue';
import type { SelectionValue } from '@/core/types/components/components';
import type { Paths } from '@/core/types/gengeric';

const props = defineProps<{
  label?: string;
  items?: T[];
  itemValue?: K;
  itemTitle?: string;
  loading?: boolean;
  multiple?: M;
  showMenuIcon?: boolean;
  hideSelection?: boolean;
}>();

const emit = defineEmits<{
  (event: 'update:search', value: string): void;
}>();

const slots = defineSlots<{
  'item'?: (props: any) => any;
  'prepend-inner'?: () => void;
}>();

const value = defineModel<(M extends true ? SelectionValue<T, K>[] : SelectionValue<T, K>) | null>({
  required: true,
});

let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const onSearchUpdate = (searchString: string) => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  
  if (searchString) {
    emit('update:search', searchString);
    return;
  }
  
  searchTimeout = setTimeout(() => {
    if (!value.value) {
      emit('update:search', '');
    }
  }, 50);
};

const autocompleteRef = ref();

const isError = ref(false);

let timeout = 0;
onUpdated(() => {
  window.clearTimeout(timeout);
  timeout = window.setTimeout(() => {
    isError.value = autocompleteRef.value?.$el.classList.contains('v-input--error');
  });
});
</script>

<style scoped lang="scss">
.e-autocomplete {
  &.v-autocomplete--active-menu {
    z-index: 999;
  }

  :deep(.v-autocomplete__selection) {
    height: auto;

    line-height: 1;
  }
}
</style>
